// Mobile API klient — singleton s injection JWT z authStore.

import { createApiClient, createMobileApi } from "@routera/shared";
import { Preferences } from "@capacitor/preferences";
import { useWorkspaceStore } from "../store/workspaceStore";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8765";

const TOKEN_KEY = "routera.auth.token";

/** Token getter — pouzity v ApiClient Bearer injection. */
async function getStoredToken(): Promise<string | null> {
  const { value } = await Preferences.get({ key: TOKEN_KEY });
  return value;
}

export async function saveToken(token: string): Promise<void> {
  await Preferences.set({ key: TOKEN_KEY, value: token });
}

export async function clearToken(): Promise<void> {
  await Preferences.remove({ key: TOKEN_KEY });
}

export async function loadToken(): Promise<string | null> {
  return getStoredToken();
}

export const API_BASE_URL = API_URL;

const client = createApiClient({
  baseUrl: API_URL,
  getToken: getStoredToken,
  getDynamicHeaders: () => {
    const id = useWorkspaceStore.getState().workspaceId;
    return id ? { "X-Workspace-Id": id } : ({ } as Record<string, string>);
  },
});

export const api = createMobileApi(client);

// ── Dávky, které musí projít i na POZADÍ ──────────────────────────────────────
// Android po ~5 min na pozadí přiškrtí `fetch` z WebView, takže GPS a eventy se
// nemají jak odeslat — i když foreground service dál sbírá. Tyhle dvě cesty proto
// jdou nativním HTTP; na webu se automaticky použije běžný klient. Viz nativeHttp.ts.
import { nativePost, isNative } from "./nativeHttp";

type BatchResult = { id: string; status: "inserted" | "deduplicated" | "rejected"; error?: string };

async function batchHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = {};
  const tok = await getStoredToken();
  if (tok) h["Authorization"] = `Bearer ${tok}`;
  const wsId = useWorkspaceStore.getState().workspaceId;
  if (wsId) h["X-Workspace-Id"] = wsId;
  return h;
}

/** Odeslání GPS dávky — nativně na Androidu, jinak přes běžný klient. */
export async function sendGpsBatch(
  positions: Array<{ id: string; shift_id: string; ts: string; lat: number; lng: number; accuracy?: number | null; battery?: number | null }>,
): Promise<BatchResult[]> {
  if (isNative()) {
    const r = await nativePost<BatchResult[]>(`${API_URL}/mobile/gps/batch`, { positions }, await batchHeaders());
    if (r.ok && Array.isArray(r.data)) return r.data;
    throw new Error(r.error ?? "gps batch failed");
  }
  return api.shift.gpsBatch(positions);
}

/** Odeslání dávky eventů — nativně na Androidu, jinak přes běžný klient. */
export async function sendEventsBatch(
  entries: Array<{ shift_id: string; event: unknown }>,
): Promise<BatchResult[]> {
  if (isNative()) {
    const r = await nativePost<BatchResult[]>(`${API_URL}/mobile/events/batch`, { events: entries }, await batchHeaders());
    if (r.ok && Array.isArray(r.data)) return r.data;
    throw new Error(r.error ?? "events batch failed");
  }
  return api.shift.eventsBatch(entries as any);
}


// ── Nový model počítárny: materiál k odvozu (odeslané zásilky pro řidiče) ──────
export interface ZasilkaMaterial {
  id: string;
  cislo: number;
  stav: string;                 // 'vytvoreno' = naložit, 'predano' = doručit
  prijemce: string | null;
  klient: string | null;
  branch_id: string | null;
  trasa: string | null;
  /** Vazba na instanci zastávky dne (`den_zastavka.id`). Když ji materiál i zastávka
   *  mají, páruje se přes ni — přežije to dispečerské přehození trasy. NULL u
   *  materiálu nachystaného na neotisknutý den → fallback na `client::branch_id`. */
  den_zastavka_id: string | null;
  jednotky: Array<{ typ: string; parent_id: string | null; branch_id: string | null; klient: string | null; pecete: string[] }>;
}

export const zasilkyMaterial = {
  list: () => client.get<{ material: ZasilkaMaterial[] }>("/mobile/zasilky/material").then((r) => r.material),
  nalozeno: (id: string) => client.post(`/mobile/zasilky/${id}/nalozeno`),
  /** Doručení = jméno + podpis PŘEBÍRAJÍCÍ osoby (portál F3) — vidí ho počítárna i pobočka. */
  doruceno: (id: string, body?: { jmeno?: string; podpis?: string; lat?: number; lng?: number }) =>
    client.post(`/mobile/zasilky/${id}/doruceno`, body ?? {}),
  nedoruceno: (id: string, poznamka?: string) => client.post(`/mobile/zasilky/${id}/nedoruceno`, { poznamka }),
};

// ── Ohlášené odvody z klientského portálu (F3) ────────────────────────────────
export interface OdvodExpectedJbo {
  id: string;
  pecet: string;
  ohlaska_id: string;
}

export const odvodApi = {
  /** Čekající ohlášené JBO pobočky — jen pečetě, sumy řidič nikdy nevidí. */
  expected: (clientName: string, branchId: string) =>
    client.get<{ jbo: OdvodExpectedJbo[] }>(
      `/mobile/odvod/expected?client=${encodeURIComponent(clientName)}&branch_id=${encodeURIComponent(branchId)}`
    ).then((r) => r.jbo),
};

/** Pečetě všech kusů zásilky (pro zobrazení „co vezeš"). */
export function zasilkaSeals(z: ZasilkaMaterial): string[] {
  return z.jednotky.flatMap((j) => j.pecete);
}

// ── Pool materiálu (přestavba 2026-09-22) ────────────────────────────────────
// Materiál je zásoba s polohou, ne položka přiřazená k trase. Mobilka se ráno
// zeptá poolu „stojím na svém depu a mám tyhle zastávky, co tu pro ně máš"
// a nárok vzniká až potvrzením nakládky.

export interface MaterialSacek {
  id:           string;
  pecet:        string;
  typ:          string;          // 'material' | 'vymena'
  cil_sheet:    string;
  cil_id:       string;
  cilove_depo:  string | null;
  depo:         string | null;
  stav:         string;
  poznamka:     string | null;
  dnu_v_poolu:  number | null;
}

export const materialApi = {
  /** Co pro mě na mém depu leží. Jen čte — nic se tím nerezervuje. */
  kNalozeni: () =>
    client.get<{ depo: string | null; sacky: MaterialSacek[]; zprava: string | null }>(
      "/mobile/material/k-nalozeni"),

  /** Potvrzení nakládky. Sken i ruční odškrtnutí končí stejně — seznamem pečetí. */
  nalozeno: (pecete: string[]) =>
    client.post<{ ok: boolean;
                  nalozeno: Array<{ id: string; pecet: string }>;
                  nepovedlo: Array<{ pecet: string; duvod: string }> }>(
      "/mobile/material/nalozeno", { pecete }),

  /** Co právě vezu. */
  nesu: () => client.get<{ sacky: MaterialSacek[] }>("/mobile/material/nesu")
    .then((r) => r.sacky),

  /** Co musím vyložit na téhle zastávce. `blokuje` = nejde ji zavřít. */
  proZastavku: (klient: string, branchId: string) =>
    client.get<{ sacky: MaterialSacek[]; blokuje: boolean }>(
      `/mobile/material/pro-zastavku?klient=${encodeURIComponent(klient)}`
      + `&branch_id=${encodeURIComponent(branchId)}`),

  /** Předání na pobočce — stvrzuje se podpisem přebírající osoby. */
  doruceno: (id: string, body?: { jmeno?: string; podpis?: string; lat?: number; lng?: number }) =>
    client.post(`/mobile/material/${id}/doruceno`, body ?? {}),

  /** Nepředáno. Důvod je nepovinný. */
  nedoruceno: (id: string, duvod?: string) =>
    client.post<{ ok: boolean; vraceno_do_depa: string }>(
      `/mobile/material/${id}/nedoruceno`, { duvod }),

  /** Vykládka TRANKu na cílovém depu — hromadně, bez skenování. */
  vylozenoNaDepu: (depo: string, pecete?: string[]) =>
    client.post<{ ok: boolean; vylozeno: number; pecete: string[]; depo: string }>(
      "/mobile/material/vylozeno-na-depu", { depo, pecete }),
};

